import { Component, Input, OnChanges, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { PaymentsAdminService } from '../../features/payments/payments-admin.service';

interface WalletKeyOption {
  key: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-wallet-key-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WalletKeySelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="key"
      [placeholder]="t(placeholderKey)"
      [showClear]="showClear"
      [filter]="showSearch"
      filterBy="label,key"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`
    .selector { width: 100%; }
  `]
})
export class WalletKeySelectorComponent implements ControlValueAccessor, OnChanges {
  private readonly api = inject(PaymentsAdminService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'payments.selectors.walletKeyPlaceholder';
  @Input() ownerType: string | null = null;
  @Input() showSearch = true;
  @Input() showClear = true;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly loading = signal(false);
  readonly options = signal<WalletKeyOption[]>([]);
  readonly value = signal<string | null>(null);

  disabled = false;

  ngOnChanges(): void {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(next: string | null): void {
    this.value.set(next);
    this.onChange(next);
    this.onTouched();
  }

  private load(): void {
    this.loading.set(true);
    this.api.listWalletKeyTemplates(this.ownerType ?? undefined).subscribe({
      next: rows => {
        this.options.set(rows.map(row => ({
          key: row.key,
          label: `${this.templateLabel(row.key)} (${row.key})`
        })));
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private templateLabel(key: string): string {
    const labelKey = `payments.wallet.keyTemplates.${key}`;
    const translated = this.t(labelKey);
    return translated === labelKey ? key : translated;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
